// What a demo holds, with a delete on every row.
//
// Composed from the ordinary directory reads plus the one list of seeded ids: a demo is a company
// whose every member is seeded. The endpoints behind the buttons re-check that independently, so
// the safety does not depend on this component rendering the right list.
import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  admin_delete_seeded,
  delete_recording,
  formatApiRequestError,
  get_all_recordings,
} from "../../api";
import type { Recording } from "../../api/types";
import { useConstrainedExpandedKeys } from "../useConstrainedExpandedKeys";
import { buildSessionTitle } from "../analytics/sessionTitle";
import type { Directory } from "./directory";

const DemoContents = ({
  directory,
  onChanged,
}: {
  directory: Directory;
  onChanged: () => void;
}) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<number | "">("");
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const demos = useMemo(
    () =>
      directory.companies.filter(company => {
        const members = directory.users.filter(user => user.company_id === company.id);
        return members.length > 0 && members.every(user => directory.seeded.has(user.id));
      }),
    [directory],
  );
  const demo = demos.find(company => company.id === selected);
  const members = useMemo(
    () =>
      demo
        ? directory.users
            .filter(user => user.company_id === demo.id)
            .sort((a, b) => a.username.localeCompare(b.username))
        : [],
    [demo, directory.users],
  );
  const { expandedKeys, toggleKey } = useConstrainedExpandedKeys(members.map(user => user.id));

  useEffect(() => {
    let cancelled = false;
    get_all_recordings({ user_ids: members.map(user => user.id) })
      .then(loaded => {
        if (!cancelled) setRecordings(loaded.recordings);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(formatApiRequestError(err, t));
      });
    return () => {
      cancelled = true;
    };
  }, [members, t]);

  // An emptied demo has no members left, so the selection clears itself through `demo`.
  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      onChanged();
    } catch (err: unknown) {
      setError(formatApiRequestError(err, t));
    } finally {
      setBusy(false);
    }
  };

  // The ordinary recording delete is asynchronous server-side, so the row is dropped here rather
  // than waited for.
  const deleteSession = (recordingId: string) =>
    run(async () => {
      await delete_recording(recordingId);
      setRecordings(current => current.filter(recording => recording.id !== recordingId));
    });

  return (
    <section className="space-y-4 rounded-xl border border-outline bg-surface p-5 shadow-sm">
      <div>
        <h2 className="text-lg font-semibold text-text">{t("tour.demo_contents")}</h2>
        <p className="mt-1 text-sm text-text-dim">{t("tour.demo_contents_description")}</p>
      </div>

      <select
        value={selected}
        onChange={event => setSelected(event.target.value === "" ? "" : Number(event.target.value))}
        className="h-10 w-full max-w-md rounded-xl border border-outline bg-background-alt px-3 text-sm text-text"
      >
        <option value="">{t("tour.select_demo_company")}</option>
        {demos.map(company => (
          <option key={company.id} value={company.id}>
            {company.name} (#{company.id})
          </option>
        ))}
      </select>

      {demos.length === 0 && (
        <p className="text-sm text-text-dim">{t("tour.no_demo_companies_to_delete")}</p>
      )}

      {error && (
        <div className="rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
          {error}
        </div>
      )}

      {demo && (
        <>
          <div className="rounded-xl border border-outline">
            {members.map(user => {
              const open = expandedKeys.has(user.id);
              const sessions = recordings.filter(recording => recording.user_id === user.id);
              return (
                <div key={user.id} className="border-b border-outline last:border-b-0">
                  <div className="flex items-center gap-2 px-3 py-2">
                    <button
                      type="button"
                      onClick={() => toggleKey(user.id)}
                      aria-expanded={open}
                      aria-label={user.username}
                      className="flex items-center gap-2 text-left"
                    >
                      {open ? (
                        <ChevronDown className="h-4 w-4 text-text-dim" aria-hidden="true" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-text-dim" aria-hidden="true" />
                      )}
                      <span className="text-sm font-medium text-text">{user.username}</span>
                    </button>
                    <span className="text-xs text-text-dim">
                      {t("tour.n_sessions", { count: sessions.length })}
                    </span>
                    <button
                      type="button"
                      onClick={() => void run(() => admin_delete_seeded(demo.id, user.id))}
                      disabled={busy}
                      aria-label={t("tour.delete_demo_user", { username: user.username })}
                      className="ml-auto rounded-md p-1 text-text-dim transition hover:bg-danger/10 hover:text-danger disabled:opacity-50"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                  {open && (
                    <div className="pb-2 pl-9 pr-3">
                      {sessions.map(session => (
                        <div
                          key={session.id}
                          className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted-surface"
                        >
                          <span className="min-w-0 flex-1 truncate text-text">
                            {buildSessionTitle(undefined, session.id, session)}
                          </span>
                          <span className="shrink-0 text-xs text-text-dim">
                            {new Date(session.date_started).toLocaleDateString()}
                          </span>
                          <button
                            type="button"
                            onClick={() => void deleteSession(session.id)}
                            disabled={busy}
                            aria-label={t("tour.delete_demo_session")}
                            className="rounded-md p-1 text-text-dim transition hover:bg-danger/10 hover:text-danger disabled:opacity-50"
                          >
                            <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <button
            type="button"
            onClick={() => void run(() => admin_delete_seeded(demo.id))}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-xl border border-danger/40 px-4 py-2 text-sm font-medium text-danger transition hover:bg-danger/10 disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
            {t("tour.delete_whole_demo", { name: demo.name })}
          </button>
        </>
      )}
    </section>
  );
};

export default DemoContents;
